import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';

const BiiforBluePreset = definePreset(Aura, {
    primitive: {
        blue: {
            50: '#F0F6FF',
            100: '#D6E5FF',
            200: '#AFCFFF',
            300: '#80B3FF',
            400: '#4C89FF', // Logo - Bleu secondaire
            500: '#0057FF', // Logo - Bleu principal
            600: '#004CDB',
            700: '#003FB8',
            800: '#002E8A',
            900: '#001E5C',
            950: '#002171'
        },
        green: {
            50: '#E6F9F1',
            100: '#B3F0D9',
            200: '#80E0C1',
            300: '#4DD7A9',
            400: '#25C685',
            500: '#1BAF76',
            600: '#1A9F68',
            700: '#168759',
            800: '#136A4B',
            900: '#0F523C'
        },
        yellow: {
            50: '#FFF4D6',
            100: '#FFE199',
            200: '#FFD15C',
            300: '#FFCC39',
            400: '#FFBC25',
            500: '#FFA000',
            600: '#FF9000',
            700: '#B67F00',
            800: '#8F6A00',
            900: '#705500'
        },
        red: {
            50: '#FFE6E6',
            100: '#FFB7B9',
            200: '#FFA8AA',
            300: '#FF8C8E',
            400: '#FF6163',
            500: '#FF4649',
            600: '#E63946',
            700: '#D03A3A',
            800: '#B32D2D',
            900: '#9B1F1F'
        },
        purple: {
            50: '#F3E9FF',
            100: '#E0C2FF',
            200: '#C699FF',
            300: '#9F70FF',
            400: '#8A59FF',
            500: '#7C4DFF',
            600: '#6939E6',
            700: '#582BCC',
            800: '#481DAE',
            900: '#38128F'
        },
        // Framboise — help/aide + accent de marque Biifor (recalibrage DS ; nom `pink` car PrimeFlex n'a pas d'utilitaires `fuchsia`)
        pink: {
            50: '#FFEEFF',
            100: '#FFDAFB',
            200: '#FFBAF3',
            300: '#FB91E6',
            400: '#EE6ED6',
            500: '#D451BE',
            600: '#B83DA4',
            700: '#983187',
            800: '#752768',
            900: '#551D4B'
        },
        // Ambre — accent de marque Biifor (recalibrage DS ; nom `orange` car PrimeFlex n'a pas d'utilitaires `amber`)
        orange: {
            50: '#FFF3E6',
            100: '#FFE4CB',
            200: '#F9CCA0',
            300: '#EDAD6B',
            400: '#DF933D',
            500: '#C6790F',
            600: '#AB6500',
            700: '#8D5200',
            800: '#6D4000',
            900: '#4F2E05'
        },
        // Palettes par défaut d'Aura, recopiées ici pour pouvoir les redéfinir
        // sans perdre le mapping sémantique (surface = slate en light, zinc en dark).
        emerald: {
            50: '#ecfdf5',
            100: '#d1fae5',
            200: '#a7f3d0',
            300: '#6ee7b7',
            400: '#34d399',
            500: '#10b981',
            600: '#059669',
            700: '#047857',
            800: '#065f46',
            900: '#064e3b',
            950: '#022c22'
        },
        lime: {
            50: '#f7fee7',
            100: '#ecfccb',
            200: '#d9f99d',
            300: '#bef264',
            400: '#a3e635',
            500: '#84cc16',
            600: '#65a30d',
            700: '#4d7c0f',
            800: '#3f6212',
            900: '#365314',
            950: '#1a2e05'
        },
        // Sarcelle — info + accent de marque Biifor (remplace l'ancien `cyan`)
        teal: {
            50: '#DFFDFC',
            100: '#B7F8F7',
            200: '#74ECEB',
            300: '#0FD6D5',
            400: '#00BEBC',
            500: '#00A2A1',
            600: '#008989',
            700: '#007070',
            800: '#005757',
            900: '#003F3F'
        },
        sky: {
            50: '#f0f9ff',
            100: '#e0f2fe',
            200: '#bae6fd',
            300: '#7dd3fc',
            400: '#38bdf8',
            500: '#0ea5e9',
            600: '#0284c7',
            700: '#0369a1',
            800: '#075985',
            900: '#0c4a6e',
            950: '#082f49'
        },
        indigo: {
            50: '#eef2ff',
            100: '#e0e7ff',
            200: '#c7d2fe',
            300: '#a5b4fc',
            400: '#818cf8',
            500: '#6366f1',
            600: '#4f46e5',
            700: '#4338ca',
            800: '#3730a3',
            900: '#312e81',
            950: '#1e1b4b'
        },
        violet: {
            50: '#f5f3ff',
            100: '#ede9fe',
            200: '#ddd6fe',
            300: '#c4b5fd',
            400: '#a78bfa',
            500: '#8b5cf6',
            600: '#7c3aed',
            700: '#6d28d9',
            800: '#5b21b6',
            900: '#4c1d95',
            950: '#2e1065'
        },
        rose: {
            50: '#fff1f2',
            100: '#ffe4e6',
            200: '#fecdd3',
            300: '#fda4af',
            400: '#fb7185',
            500: '#f43f5e',
            600: '#e11d48',
            700: '#be123c',
            800: '#9f1239',
            900: '#881337',
            950: '#4c0519'
        },
        slate: {
            50: '#f8fafc',
            100: '#f1f5f9',
            200: '#e2e8f0',
            300: '#cbd5e1',
            400: '#94a3b8',
            500: '#64748b',
            600: '#475569',
            700: '#334155',
            800: '#1e293b',
            900: '#0f172a',
            950: '#020617'
        },
        gray: {
            50: '#f9fafb',
            100: '#f3f4f6',
            200: '#e5e7eb',
            300: '#d1d5db',
            400: '#9ca3af',
            500: '#6b7280',
            600: '#4b5563',
            700: '#374151',
            800: '#1f2937',
            900: '#111827',
            950: '#030712'
        },
        zinc: {
            50: '#fafafa',
            100: '#f4f4f5',
            200: '#e4e4e7',
            300: '#d4d4d8',
            400: '#a1a1aa',
            500: '#71717a',
            600: '#52525b',
            700: '#3f3f46',
            800: '#27272a',
            900: '#18181b',
            950: '#09090b'
        },
        neutral: {
            50: '#fafafa',
            100: '#f5f5f5',
            200: '#e5e5e5',
            300: '#d4d4d4',
            400: '#a3a3a3',
            500: '#737373',
            600: '#525252',
            700: '#404040',
            800: '#262626',
            900: '#171717',
            950: '#0a0a0a'
        },
        stone: {
            50: '#fafaf9',
            100: '#f5f5f4',
            200: '#e7e5e4',
            300: '#d6d3d1',
            400: '#a8a29e',
            500: '#78716c',
            600: '#57534e',
            700: '#44403c',
            800: '#292524',
            900: '#1c1917',
            950: '#0c0a09'
        }
    },
    semantic: {
        primary: {
            50: '{blue.50}',
            100: '{blue.100}',
            200: '{blue.200}',
            300: '{blue.300}',
            400: '{blue.400}',
            500: '{blue.500}',
            600: '{blue.600}',
            700: '{blue.700}',
            800: '{blue.800}',
            900: '{blue.900}',
            950: '{blue.950}'
        }
    },
    components: {
        badge: {
            root: {
                fontSize: '0.8rem',
            }
        },
        breadcrumb: {
            root: {
                background: 'inherit',
                padding: '1.5rem 2rem .5rem 2rem',
            },
        },
        button: {
            colorScheme: {
                light: {
                    root: {
                        warn: {
                            background: '{yellow.600}',
                            borderColor: '{yellow.600}',
                            color: '#ffffff',
                        }
                    },
                    outlined: {
                        warn: {
                            color: '{yellow.600}',
                            borderColor: '{yellow.600}',
                        }
                    },
                    text: {
                        warn: {
                            color: '{yellow.600}',
                        }
                    }
                }
            }
        },
        datatable: {
            columnTitle: {
                fontWeight: '400'
            }
        },
        dataview: {
            content: {
                borderRadius: '{border.radius.xl}'
            },
        },
        divider: {
            content: {
                background: 'transparent',
            }
        },
        fieldset: {
            legend: {
                borderColor: '{fieldset.border.color}'
            }
        },
        message: {
            root: {
                borderWidth: '1px',
            },
            content: {
                padding: '1rem 2rem 1rem 1rem',
            }
        },
        tabs: {
            tablist: {
                background: 'transparent',
            },
            tab: {
                background: 'transparent',
                fontWeight: '400',
            },
            tabpanel: {
                background: 'transparent',
            },
        },
        tag: {
            colorScheme: {
                light: {
                    warn: {
                        background: '{yellow.100}',
                        color: '{yellow.700}',
                    }
                }
            }
        }
    }
});

export default BiiforBluePreset;